import React, { useState } from 'react';
import styled from 'styled-components';
import { supabase } from '../utils/supabase';

// Simple icon components matching existing style
const Check = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const AlertCircle = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const Package = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const Loader = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
  </svg>
);

// Styled components based on the app's existing style
const Calculator = styled.div`
  background-color: white;
  border-radius: 24px;
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 800px; /* Increased from 680px */
  overflow: hidden;
  margin: 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(5px);
`;

const CalculatorHeader = styled.div`
  background-color: var(--secondary-color);
  color: white;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-image: linear-gradient(135deg, var(--secondary-color) 0%, #003366 100%);
  position: relative;
  overflow: hidden;
  border-radius: 24px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0) 60%);
    border-radius: 24px;
  }
`;

const HeaderTitle = styled.div`
  position: relative;
  z-index: 1;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.01em;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  p {
    font-size: var(--text-md);
    color: rgba(255, 255, 255, 0.95);
    margin-top: 0.75rem;
    max-width: 550px;
    line-height: 1.6;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    font-weight: 400;
    letter-spacing: 0.01em;
  }
`;

const SuccessIcon = styled.div`
  background-color: rgba(255, 143, 0, 0.3);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FF8F00;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.3);
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    border: 2px solid rgba(255, 143, 0, 0.2);
    z-index: -1;
  }
  
  svg {
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
  }
`;

const CalculatorBody = styled.div`
  padding: 3.5rem 3.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: transparent;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.75));
  backdrop-filter: blur(10px);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
  color: var(--secondary-color);
  font-size: var(--text-sm);
`;

const RequiredStar = styled.span`
  color: var(--danger-color, #e74c3c);
  margin-left: 0.25rem;
`;

const FormInput = styled.input`
  width: 90%;
  padding: 0.9rem 1.25rem;
  border: 2px solid rgba(52, 152, 219, 0.15);
  border-radius: 8px;
  font-size: var(--text-md);
  text-align: center;
  box-shadow: none;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.5);
  display: block;
  margin: 0 auto;
  backdrop-filter: blur(4px);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
    background-color: rgba(255, 255, 255, 0.8);
  }
  
  &:hover {
    border-color: rgba(52, 152, 219, 0.3);
    background-color: rgba(255, 255, 255, 0.7);
  }
`;

const FormSelect = styled.select`
  width: 90%;
  padding: 0.9rem 1.25rem;
  border: 2px solid rgba(52, 152, 219, 0.15);
  border-radius: 8px;
  font-size: var(--text-md);
  background-color: rgba(255, 255, 255, 0.5);
  text-align: center;
  box-shadow: none;
  text-align-last: center;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233498db' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  cursor: pointer;
  display: block;
  margin: 0 auto;
  backdrop-filter: blur(4px);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
    background-color: rgba(255, 255, 255, 0.8);
  }
  
  &:hover {
    border-color: rgba(52, 152, 219, 0.3);
    background-color: rgba(255, 255, 255, 0.7);
  }
  
  option {
    text-align: left;
    font-size: var(--text-md);
  }
`;

const ErrorMessage = styled.p`
  margin-top: 0.5rem;
  color: var(--danger-color, #e74c3c);
  font-size: var(--text-sm);
  text-align: center;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* Change to 4 columns */
  gap: 1.5rem;
  width: 100%;
  max-width: 760px; /* Increased from 680px */
  align-items: start;
  justify-content: center;
  margin: 0.5rem 0;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  margin: 0 auto;
  justify-content: center;
`;

const QuantityButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(52, 152, 219, 0.1);
  font-size: 1.25rem;
  font-weight: 500;
  transition: all 0.2s ease;
  color: var(--primary-color);
  
  &:first-child {
    border-top-left-radius: var(--rounded-md);
    border-bottom-left-radius: var(--rounded-md);
  }
  
  &:last-child {
    border-top-right-radius: var(--rounded-md);
    border-bottom-right-radius: var(--rounded-md);
  }
  
  &:hover {
    background-color: rgba(52, 152, 219, 0.2);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  height: 40px;
  border-top: 2px solid rgba(52, 152, 219, 0.15);
  border-bottom: 2px solid rgba(52, 152, 219, 0.15);
  border-left: none;
  border-right: none;
  text-align: center;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--secondary-color);
  background-color: rgba(255, 255, 255, 0.7);
  
  &:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.9);
  }
  
  /* Hide number spinner */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const CalculateButton = styled.button`
  width: 100%;
  max-width: 260px;
  padding: 1.2rem 1.5rem;
  background: linear-gradient(to right, var(--primary-color), #2980b9);
  color: white;
  font-weight: 500;
  border-radius: 8px;
  font-size: var(--text-md);
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(52, 152, 219, 0.2);
  letter-spacing: 0.3px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }
  
  &:hover {
    background: linear-gradient(to right, #2980b9, var(--primary-color));
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(52, 152, 219, 0.3);
    
    &::before {
      transform: translateX(100%);
    }
  }
  
  &:disabled {
    background-color: var(--primary-light);
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  svg {
    margin-right: 0.75rem;
  }
  
  .spinner {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const InfoBox = styled.div`
  background-color: rgba(52, 152, 219, 0.08);
  border: 1px solid rgba(52, 152, 219, 0.2);
  border-radius: var(--rounded-md);
  padding: 1.25rem 1.25rem;
  margin-top: 1.75rem;
  display: flex;
  align-items: flex-start;
  text-align: left;
  max-width: 500px;
  box-shadow: 0 2px 6px rgba(52, 152, 219, 0.06);
`;

const InfoIcon = styled.div`
  color: var(--primary-color);
  flex-shrink: 0;
  margin-right: 0.75rem;
  margin-top: 0.1rem;
  font-size: 0.9rem;
`;

const InfoContent = styled.div`
  width: 100%;
  
  h3 {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--primary-color);
    margin: 0;
  }
  
  p {
    margin-top: 0.4rem;
    font-size: var(--text-xs);
    color: var(--secondary-color);
    line-height: 1.4;
  }
  
  .highlight {
    font-weight: 600;
    display: block;
    margin-top: 0.5rem;
    color: var(--primary-color);
    font-size: var(--text-xs);
  }
`;

const ResultCard = styled.div`
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--rounded-lg);
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 540px;
  text-align: center;
  border: 1px solid rgba(52, 152, 219, 0.15);
  backdrop-filter: blur(8px);
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-bottom: 0.75rem;
    margin-bottom: 0.3rem;
  }
  
  &.total {
    font-weight: 700;
    color: var(--primary-color);
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 2px solid rgba(52, 152, 219, 0.15);
  }
`;

const ResultLabel = styled.span`
  color: var(--secondary-color);
  font-size: var(--text-sm);
`;

const ResultValue = styled.span`
  font-weight: 700;
  font-size: var(--text-sm);
  color: ${props => props.highlight ? 'var(--primary-color)' : '#2d3748'};
  ${props => props.large && `
    font-size: var(--text-lg);
    letter-spacing: 0.3px;
  `}
`;

const ResetButton = styled.button`
  width: auto;
  min-width: 180px;
  margin: 1.5rem auto 0;
  display: block;
  padding: 0.8rem 1.5rem;
  background-color: transparent;
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 8px;
  font-size: var(--text-sm);
  transition: all 0.3s ease;
  border: 1px solid var(--primary-color);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba(52, 152, 219, 0.05), rgba(52, 152, 219, 0.1), rgba(52, 152, 219, 0.05));
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }
  
  &:hover {
    background-color: rgba(52, 152, 219, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);
    
    &::before {
      transform: translateX(100%);
    }
  }
`;

const CopyButton = styled.button`
  width: auto;
  min-width: 180px;
  margin: 1.5rem auto 0.5rem; /* Adjust margin to accommodate two buttons */
  display: block;
  padding: 0.8rem 1.5rem;
  background-color: var(--primary-color);
  color: white;
  font-weight: 500;
  border-radius: 8px;
  font-size: var(--text-sm);
  transition: all 0.3s ease;
  border: 1px solid var(--primary-color);
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    background-color: #2980b9;
    border-color: #2980b9;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ErrorAlert = styled.div`
  background-color: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: var(--rounded-md);
  padding: 0.75rem 1rem;
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  svg {
    margin-right: 0.5rem;
    flex-shrink: 0;
    color: #e74c3c;
  }
  
  p {
    margin: 0;
    font-size: var(--text-sm);
    color: #e74c3c;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(to right, #FF8F00, #FFA733); /* Subtle orange gradient */
  color: white;
  border: none;
  border-radius: 6px; /* Slightly larger border radius */
  padding: 0.4rem 0.8rem; /* Slightly more padding */
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.3px; /* Added letter spacing */
  margin-left: 1rem;
  cursor: pointer;
  transition: all 0.3s ease; /* Smoother transition */
  box-shadow: 0 4px 8px rgba(255, 143, 0, 0.2); /* More prominent box shadow */
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  position: relative; /* Needed for potential future animations */
  overflow: hidden; /* Needed for potential future animations */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }
  
  &:hover {
    background: linear-gradient(to right, #FFA733, #FF8F00); /* Reverse gradient on hover */
    transform: translateY(-2px); /* Lift effect on hover */
    box-shadow: 0 6px 12px rgba(255, 143, 0, 0.3); /* Enhanced shadow on hover */
    
    &::before {
      transform: translateX(100%); /* Animation on hover */
    }
  }
  
  &:active {
    transform: translateY(-1px); /* Press effect */
    box-shadow: 0 2px 4px rgba(255, 143, 0, 0.2); /* Reduced shadow on active */
  }
  
  svg {
    margin-right: 0.4rem;
  }
`;

const ShippingCalculator = () => {
  const [formData, setFormData] = useState({
    country: '',
    zipcode: '',
    truckHeight: 2400, // Added truckHeight, default to 2400
    decks: 0,
    poles: 0,
    poleLength: 0 // Set default pole length to 0
  });

  const [errors, setErrors] = useState({
    country: '',
    zipcode: '',
    truckHeight: '', // Added truckHeight error state
    decks: '',
    poles: '',
    poleLength: '' // Add error state for pole length
  });

  const [calculationState, setCalculationState] = useState({
    isCalculating: false,
    isComplete: false,
    error: null,
    result: null
  });

  const [isCopied, setIsCopied] = useState(false);
  const [isAdjusted, setIsAdjusted] = useState(false);

  // Available shipping countries with country flags
  const countries = [
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      let processedValue = value;
      // For numeric fields, ensure we have a number or empty string
      if (['truckHeight', 'decks', 'poles', 'poleLength'].includes(field)) { // Added truckHeight
        // If it's an empty string or 0, store as-is to allow clearing the field
        if (value === '' || value === 0) {
          processedValue = value;
        } else {
          // Otherwise ensure it's a number
          processedValue = Number(value);
        }
      }

      const updatedFormData = {
        ...prev,
        [field]: processedValue
      };
      
      // Real-time validation for poles-to-decks ratio and other relevant errors
      if (field === 'truckHeight') { // Added handling for truckHeight
        const truckHeightValue = parseFloat(processedValue) || 0;
        const newErrors = { ...errors };
        if (truckHeightValue <= 0) {
          newErrors.truckHeight = 'Truck height is required and must be greater than 0 mm';
        } else {
          // Clear the specific "Truck height is required" error
          if (newErrors.truckHeight && newErrors.truckHeight.includes('Truck height is required')) {
            newErrors.truckHeight = '';
          }
          // Clear the "cannot be negative" error if it was previously set and now positive
          if (newErrors.truckHeight && newErrors.truckHeight.includes('Truck height cannot be negative')) {
             newErrors.truckHeight = '';
          }
        }
        setErrors(newErrors);
      } else if (field === 'poles' || field === 'decks') {
        const decks = parseFloat(field === 'decks' ? processedValue : updatedFormData.decks) || 0;
        const poles = parseFloat(field === 'poles' ? processedValue : updatedFormData.poles) || 0;
        const poleLength = parseFloat(updatedFormData.poleLength) || 0;

        const newErrors = { ...errors }; // Start with existing errors

        // Poles/Decks Ratio Validation
        const maxPoles = decks * 40;
        if (poles > maxPoles && decks > 0) {
          newErrors.poles = `Pole limit exceeded for ${decks} deck${decks > 1 ? 's' : ''}. Please add decks or reduce poles.`; // Simplified message
        } else {
          // Clear the specific poles/decks ratio error if the condition is met
          if (newErrors.poles && newErrors.poles.includes('Pole limit exceeded')) { // Updated check to match current error message
            newErrors.poles = '';
          }
        }
        
        // Rule: Cannot order only poles (decks must be > 0 if poles > 0)
        if (poles > 0 && decks <= 0) {
            newErrors.decks = 'Cannot order only poles. Please add at least 1 deck.';
        } else {
             // Clear the specific "Cannot order only poles" error
            if (newErrors.decks && newErrors.decks.includes('Cannot order only poles')) {
                newErrors.decks = '';
            }
        }
        
        // Rule: At least decks must be greater than 0 if no poles are specified
        if (decks <= 0 && poles <= 0) {
            newErrors.decks = 'Please enter at least 1 deck.';
        } else {
             // Clear the specific "Please enter at least 1 deck" error
            if (newErrors.decks && newErrors.decks.includes('Please enter at least 1 deck')) {
                newErrors.decks = '';
            }
        }

        // Validate poles and pole length based on new logic (re-check these as well)
        if (poles > 0 && poleLength <= 0) {
          newErrors.poleLength = 'Pole length is required when poles are included';
        } else {
             if (newErrors.poleLength && newErrors.poleLength.includes('Pole length is required')) {
                newErrors.poleLength = '';
            }
        }

        if (poleLength > 0) {
          if (poles <= 0) {
            newErrors.poles = 'Number of poles is required when pole length is specified';
          } else {
             if (newErrors.poles && newErrors.poles.includes('Number of poles is required')) {
                newErrors.poles = '';
            }
          }
           if (decks <= 0) {
            newErrors.decks = 'Number of decks is required when pole length is specified';
          } else {
             if (newErrors.decks && newErrors.decks.includes('Number of decks is required')) {
                newErrors.decks = '';
            }
          }
        } else {
             if (newErrors.poles && newErrors.poles.includes('Number of poles is required')) {
                newErrors.poles = '';
            }
             if (newErrors.decks && newErrors.decks.includes('Number of decks is required')) {
                newErrors.decks = '';
            }
        }


        // Re-validate max limits for poles and decks
        if (poles > 1000) {
            newErrors.poles = 'Maximum 1000 poles allowed per order';
        } else {
             if (newErrors.poles && newErrors.poles.includes('Maximum 1000 poles allowed')) {
                newErrors.poles = '';
            }
        }

         if (decks > 1000) {
            newErrors.decks = 'Maximum 1000 decks allowed per order';
        } else {
             if (newErrors.decks && newErrors.decks.includes('Maximum 1000 decks allowed')) {
                newErrors.decks = '';
            }
        }


        // Re-validate negative values
        if (decks < 0) {
             newErrors.decks = 'Number of decks cannot be negative';
        } else {
             if (newErrors.decks && newErrors.decks.includes('Number of decks cannot be negative')) {
                newErrors.decks = '';
            }
        }

        if (poles < 0) {
             newErrors.poles = 'Number of poles cannot be negative';
        } else {
             if (newErrors.poles && newErrors.poles.includes('Number of poles cannot be negative')) {
                newErrors.poles = '';
            }
        }

         if (poleLength < 0) {
             newErrors.poleLength = 'Pole length cannot be negative';
        } else {
             if (newErrors.poleLength && newErrors.poleLength.includes('Pole length cannot be negative')) {
                newErrors.poleLength = '';
            }
        }


        setErrors(newErrors); // Update errors state
      } else {
         // For other fields, just clear the error for that field
        if (errors[field]) {
          setErrors(prev => ({
            ...prev,
            [field]: ''
          }));
        }
      }


      return updatedFormData;
    });
  };
  
  // Helper function to automatically adjust decks based on poles
  const adjustDecksForPoles = () => {
    const poles = parseFloat(formData.poles) || 0;
    if (poles > 0) {
      const requiredDecks = Math.ceil(poles / 40);
      handleInputChange('decks', requiredDecks);
      setIsAdjusted(true);
      setTimeout(() => setIsAdjusted(false), 2000); // Revert back to original text after 2 seconds
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate country
    if (!formData.country) {
      newErrors.country = 'Please select a country';
      isValid = false;
    }

    // Validate zipcode
    if (!formData.zipcode) {
      newErrors.zipcode = 'Please enter a zipcode/postal code';
      isValid = false;
    }

    // Validate decks
    const decks = parseFloat(formData.decks) || 0;
    const truckHeight = parseFloat(formData.truckHeight) || 0; // Added truckHeight validation

    if (truckHeight <= 0) { // Truck height must be greater than 0
      newErrors.truckHeight = 'Truck height is required and must be greater than 0 mm';
      isValid = false;
    }

    if (decks < 0) { // Decks cannot be negative
      newErrors.decks = 'Number of decks cannot be negative';
      isValid = false;
    } else if (decks > 1000) {
      newErrors.decks = 'Maximum 1000 decks allowed per order';
      isValid = false;
    }

    // Validate poles and pole length based on new logic
    const poles = parseFloat(formData.poles) || 0;
    const poleLength = parseFloat(formData.poleLength) || 0;

    if (poles > 0 && poleLength <= 0) {
      newErrors.poleLength = 'Pole length is required when poles are included';
      isValid = false;
    }

    if (poleLength > 0) {
      // When pole length is specified, both poles and decks are required
      if (poles <= 0) {
        newErrors.poles = 'Number of poles is required when pole length is specified';
        isValid = false;
      }
      
      if (decks <= 0) {
        newErrors.decks = 'Number of decks is required when pole length is specified';
        isValid = false;
      }
    }
    
    if (poles > 1000) {
      newErrors.poles = 'Maximum 1000 poles allowed per order';
      isValid = false;
    }

    // Rule: For every 40 poles, there must be at least 1 deck
    const maxPoles = decks * 40;
    if (poles > maxPoles && decks > 0) { // Only apply this rule if there's at least one deck
      newErrors.poles = `Pole limit exceeded for ${decks} deck${decks > 1 ? 's' : ''}. Please add decks or reduce poles.`; // Simplified message
      isValid = false;
    } else if (poles > 0 && decks === 0) {
        // This case is already covered by the "Cannot order only poles" rule,
        // but adding a specific message here might be clearer.
        // However, the existing rule's message is sufficient.
        // Keeping this check here for clarity but not adding a new error message
        // to avoid duplicate messages for the same condition.
    }

    if (poleLength < 0) { // Pole length cannot be negative
      newErrors.poleLength = 'Pole length cannot be negative';
      isValid = false;
    }


    // Rule: Cannot order only poles (decks must be > 0 if poles > 0)
    if (poles > 0 && decks <= 0) {
        newErrors.decks = 'Cannot order only poles. Please add at least 1 deck.';
        isValid = false;
    }
    
    // Rule: At least decks must be greater than 0 if no poles are specified
    if (decks <= 0 && poles <= 0) {
        newErrors.decks = 'Please enter at least 1 deck.';
        isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const calculateShipping = async () => {
    if (!validateForm()) {
      return;
    }

    setCalculationState({
      isCalculating: true,
      isComplete: false,
      error: null,
      result: null
    });

    try {
      // Format data for the webhook in the required format
      const webhookData = [
        {
          "Country": formData.country,
          "Zipcode": formData.zipcode,
          "Truck Height": String(formData.truckHeight), // Added truckHeight
          "Amount of decks": String(formData.decks),
          "Amount of poles": String(formData.poles),
          "Pole Length": String(formData.poleLength) // Add pole length
        }
      ];
      
      // Send data to the Edge Function
      const { data: functionResponse, error: functionError } = await supabase.functions.invoke('calculate-shipping', {
        body: webhookData
      });

      if (functionError) {
        throw new Error(`Edge Function error: ${functionError.message}`);
      }
      
      // Parse the response data (Edge Function returns raw response text)
      const responseText = typeof functionResponse === 'string' ? functionResponse : JSON.stringify(functionResponse);
      let data;
      
      if (responseText && responseText.trim()) {
        try {
          const apiResponse = JSON.parse(responseText);
          
          // The response is an array, take the first item
          const responseData = Array.isArray(apiResponse) ? apiResponse[0] : apiResponse;
          
          // Extract the price from the returned data, removing the € symbol
          const priceString = responseData.price || '€0';
          const priceValue = parseFloat(priceString.replace('€', '').replace(',', '.'));
          
          data = {
            success: true,
            shipping_cost: priceValue,
            currency: 'EUR',
            pallet_places: responseData.pallet_count || responseData.pallet_places || 1, // Use pallet_count if available, fallback to pallet_places
            delivery_estimate: '3-5 business days', // Keep existing estimate or use webhook if available? Assuming keep existing for now.
            shipping_method: 'Standard', // Keep existing or use webhook if available? Assuming keep existing for now.
            weight: responseData.total_estimated_weight_kg || ((formData.decks * 15) + (formData.poles * 45)), // Use webhook weight if available, fallback to calculation
            tracking_available: true, // Keep existing or use webhook if available? Assuming keep existing for now.
            pallet_summary: responseData.pallet_summary || '' // Add pallet summary
          };
        } catch (e) {
          // Fallback if there's an issue parsing the response
          data = {
            success: true,
            shipping_cost: 12.50 + (formData.decks * 1.75) + (formData.poles * 2.5),
            currency: 'EUR',
            pallet_places: Math.ceil((formData.decks / 20) + (formData.poles / 15)),
            delivery_estimate: '3-5 business days',
            shipping_method: 'Standard',
            weight: (formData.decks * 15) + (formData.poles * 45), // kg
            tracking_available: true
          };
        }
      } else {
        // Fallback if there's no response
        data = {
          success: true,
          shipping_cost: 12.50 + (formData.decks * 1.75) + (formData.poles * 2.5),
          currency: 'EUR',
          pallet_places: Math.ceil((formData.decks / 20) + (formData.poles / 15)),
          delivery_estimate: '3-5 business days',
          shipping_method: 'Standard',
          weight: (formData.decks * 15) + (formData.poles * 45), // kg
          tracking_available: true
        };
      }

      setCalculationState({
        isCalculating: false,
        isComplete: true,
        error: null,
        result: data
      });
    } catch (error) {
      console.error("Error calculating shipping:", error);
      setCalculationState({
        isCalculating: false,
        isComplete: false,
        error: 'Failed to calculate shipping. Please try again.',
        result: null
      });
    }
  };

  const resetCalculator = () => {
    setFormData({
      country: '',
      zipcode: '',
      truckHeight: 0, // Reset truckHeight
      decks: 0, // Reset decks to 0
      poles: 0, // Reset poles to 0
      poleLength: 0 // Reset pole length to 0
    });
    
    setCalculationState({
      isCalculating: false,
      isComplete: false,
      error: null,
      result: null
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const copyResultToClipboard = async () => {
    const { result } = calculationState;
    const shippingInfo = `Shipping Quote:

Shipping to: ${countries.find(c => c.name === formData.country)?.flag} ${formData.country} - ${formData.zipcode}
Truck Height: ${formData.truckHeight} mm 
Decks: ${formData.decks} deck${formData.decks > 1 ? 's' : ''}
Poles: ${formData.poles} pole${formData.poles !== 1 ? 's' : ''}
Pole Length: ${formData.poleLength} mm
Pallet Count: ${result.pallet_places}
Estimated Weight: ${result.weight} kg

${result.pallet_summary ? `Pallet Summary:\n${result.pallet_summary.split('\n').map(item => {
      const parts = item.split(' ');
      if (parts.length >= 2) {
        const quantity = parts[0];
        const itemType = parts[1];
        const rest = parts.slice(2).join(' ');
        return `${quantity} ${itemType} ${rest}`;
      }
      return item;
    }).join('\n')}` : ''}

Total Shipping Cost: ${formatCurrency(result.shipping_cost, result.currency)}
`;

    try {
      await navigator.clipboard.writeText(shippingInfo);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Revert back to original text after 2 seconds
      console.log('Shipping info copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy shipping info: ', err);
      // Optionally, show an error message to the user
    }
  };

  if (calculationState.isComplete && calculationState.result) {
    const { result } = calculationState;
    
    return (
      <Calculator>
        <CalculatorHeader>
          <SuccessIcon>
            <Check size={24} />
          </SuccessIcon>
          <HeaderTitle>
            <h1>Shipping Quote</h1>
            <p>Your shipping cost estimate</p>
          </HeaderTitle>
        </CalculatorHeader>
        
        <CalculatorBody>
          <ResultCard>
            <ResultRow>
              <ResultLabel>Shipping to:</ResultLabel>
              <ResultValue>
                {countries.find(c => c.name === formData.country)?.flag} {formData.country} - ${formData.zipcode}
              </ResultValue>
            </ResultRow>

            <ResultRow>
              <ResultLabel>Truck Height:</ResultLabel>
              <ResultValue>{formData.truckHeight} mm</ResultValue>
            </ResultRow>
            
            <ResultRow>
              <ResultLabel>Decks:</ResultLabel>
              <ResultValue>{formData.decks} deck{formData.decks > 1 ? 's' : ''}</ResultValue>
            </ResultRow>
            
            <ResultRow>
              <ResultLabel>Poles:</ResultLabel>
              <ResultValue>{formData.poles} pole{formData.poles !== 1 ? 's' : ''}</ResultValue>
            </ResultRow>
            
            <ResultRow>
              <ResultLabel>Pole Length:</ResultLabel>
              <ResultValue>{formData.poleLength} mm</ResultValue>
            </ResultRow>

            <ResultRow>
              <ResultLabel>Pallet Count:</ResultLabel>
              <ResultValue>{result.pallet_places}</ResultValue> {/* Using pallet_places from the result object which now holds pallet_count */}
            </ResultRow>

            <ResultRow>
              <ResultLabel>Estimated Weight:</ResultLabel>
              <ResultValue>{result.weight} kg</ResultValue> {/* Using weight from the result object which now holds total_estimated_weight_kg */}
            </ResultRow>

            {result.pallet_summary && ( // Only show pallet summary if it exists
              <ResultRow>
                <ResultLabel>Pallet Summary:</ResultLabel>
                <ResultValue>
                  {result.pallet_summary.split('\n').map((item, index) => {
                    const parts = item.split(' ');
                    if (parts.length >= 2) {
                      const quantity = parts[0];
                      const itemType = parts[1];
                      const rest = parts.slice(2).join(' ');
                      return (
                        <div key={index}>
                          <strong>{quantity} {itemType}</strong> {rest}
                        </div>
                      );
                    }
                    return <div key={index}>{item}</div>;
                  })}
                </ResultValue>
              </ResultRow>
            )}
            
            <ResultRow>
              <ResultLabel>Total Shipping Cost:</ResultLabel>
              <ResultValue highlight large>
                {formatCurrency(result.shipping_cost, result.currency)}
              </ResultValue>
            </ResultRow>
          </ResultCard>
          
          <CopyButton onClick={copyResultToClipboard}>
            {isCopied ? 'Copied!' : 'Copy Quote'}
          </CopyButton>
          <ResetButton onClick={resetCalculator}>
            Calculate Another
          </ResetButton>
        </CalculatorBody>
      </Calculator>
    );
  }

  return (
    <Calculator>
      <CalculatorHeader>
        <SuccessIcon>
          <Package size={24} />
        </SuccessIcon>
        <HeaderTitle>
          <h1>Shipping Calculator</h1>
          <p>Calculate shipping costs for your order</p>
        </HeaderTitle>
      </CalculatorHeader>
      
      <CalculatorBody>
        <FormGroup>
          <FormLabel>Country</FormLabel>
          <FormSelect
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
          >
            <option value="">Select a country</option>
            {countries.map(country => (
              <option key={country.code} value={country.name}>
                {country.flag} {country.name}
              </option>
            ))}
          </FormSelect>
          {errors.country && <ErrorMessage>{errors.country}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormLabel>Zipcode / Postal Code</FormLabel>
          <FormInput
            type="text"
            value={formData.zipcode}
            onChange={(e) => handleInputChange('zipcode', e.target.value)}
            placeholder="Enter your zipcode"
          />
          {errors.zipcode && <ErrorMessage>{errors.zipcode}</ErrorMessage>}
        </FormGroup>
        
        <GridContainer>
          <FormGroup>
            <FormLabel>
              Truck height (mm) <RequiredStar>*</RequiredStar>
            </FormLabel>
            <QuantityControl>
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('truckHeight', Math.max(1, formData.truckHeight - 1))}
              >
                −
              </QuantityButton>
              <QuantityInput
                type="number"
                value={formData.truckHeight}
                onChange={(e) => handleInputChange('truckHeight', parseInt(e.target.value) || 0)}
              />
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('truckHeight', formData.truckHeight + 1)}
              >
                +
              </QuantityButton>
            </QuantityControl>
            {errors.truckHeight && <ErrorMessage>{errors.truckHeight}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <FormLabel>
              Number of Decks <RequiredStar>*</RequiredStar>
            </FormLabel>
            <QuantityControl>
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('decks', Math.max(0, formData.decks - 1))}
              >
                −
              </QuantityButton>
              <QuantityInput
                type="number"
                value={formData.decks}
                onChange={(e) => handleInputChange('decks', parseInt(e.target.value) || 0)}
              />
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('decks', Math.min(1000, formData.decks + 1))}
              >
                +
              </QuantityButton>
            </QuantityControl>
            {errors.decks && <ErrorMessage>{errors.decks}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>
              Number of Poles
            </FormLabel>
            <QuantityControl>
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('poles', Math.max(0, formData.poles - 1))}
              >
                −
              </QuantityButton>
              <QuantityInput
                type="number"
                value={formData.poles}
                onChange={(e) => handleInputChange('poles', parseInt(e.target.value) || 0)}
              />
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('poles', Math.min(1000, formData.poles + 1))}
                disabled={formData.poles >= (formData.decks * 40) && formData.decks > 0}
                style={{
                  opacity: formData.poles >= (formData.decks * 40) && formData.decks > 0 ? 0.5 : 1,
                  cursor: formData.poles >= (formData.decks * 40) && formData.decks > 0 ? 'not-allowed' : 'pointer'
                }}
              >
                +
              </QuantityButton>
            </QuantityControl>
            {errors.poles && <ErrorMessage>{errors.poles}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <FormLabel>
              Pole Length (mm)
            </FormLabel>
            <QuantityControl>
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('poleLength', Math.max(0, formData.poleLength - 1))}
              >
                −
              </QuantityButton>
              <QuantityInput
                type="number"
                value={formData.poleLength}
                onChange={(e) => handleInputChange('poleLength', parseInt(e.target.value) || 0)}
              />
              <QuantityButton
                type="button"
                onClick={() => handleInputChange('poleLength', formData.poleLength + 1)} // No upper limit specified, allow incrementing
              >
                +
              </QuantityButton>
            </QuantityControl>
            {errors.poleLength && <ErrorMessage>{errors.poleLength}</ErrorMessage>}
          </FormGroup>
        </GridContainer>

        {calculationState.error && (
          <ErrorAlert>
            <AlertCircle size={18} />
            <p>{calculationState.error}</p>
          </ErrorAlert>
        )}

        
        <CalculateButton
          onClick={calculateShipping}
          disabled={calculationState.isCalculating}
        >
          {calculationState.isCalculating ? (
            <>
              <Loader size={20} className="spinner" />
              Calculating...
            </>
          ) : (
            <>
              <Package size={20} />
              Calculate Shipping
            </>
          )}
        </CalculateButton>
        
        <InfoBox>
          <InfoIcon>
            <AlertCircle size={14} />
          </InfoIcon>
          <InfoContent>
            <h3>Shipping Information</h3>
            <p>
              This quote provides a carefully calculated estimate based on current rates and dimensions. 
              While highly accurate, final costs may be optimized during processing.
              International orders may be subject to additional customs fees.
            </p>
          </InfoContent>
        </InfoBox>
      </CalculatorBody>
    </Calculator>
  );
};

export default ShippingCalculator;
